import {
  ClientOrganizationProfileRoleEnum,
  ClientOrganizationTagsEnum
} from '@DigitaleDoerfer/digitale-doerfer-api';
import type { ClientOrganizationUpdateRequest } from '@DigitaleDoerfer/digitale-doerfer-api';
import type { FC } from 'react';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';

import { useDeleteOrganization } from '@/features/api/hooks/grapevine-organization/useDeleteOrganization';
import { useGetOrganizationById } from '@/features/api/hooks/grapevine-organization/useGetOrganizationById';
import { useUpdateOrganization } from '@/features/api/hooks/grapevine-organization/useUpdateOrganization';
import { usePopup } from '@/features/popup/provider/usePopup';
import { OrganizationEditForm } from '@/features/profile-setting/components/organization-edit/OrganizationEditForm';
import { TerritorialAuthorityEditForm } from '@/features/profile-setting/components/organization-edit/TerritorialAuthorityEditForm';
import type {
  OrganizationEditSubmit,
  TerritorialAuthorityEditSubmit
} from '@/features/profile-setting/components/organization-edit/types';
import { ProfileSettingRoute } from '@/features/profile-setting/navigation/profileSettingRoute';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { NavigationBar } from '@/navigation/components/navbar/NavigationBar';
import { NavBarType } from '@/navigation/types/navbar';
import type { AppNativeStackScreenProps } from '@/navigation/types/navigation';
import { AppRoute, TabRoute } from '@/navigation/types/navigation';
import { OrganizationAccessGuard } from '@/shared/components/organization';
import { StateView } from '@/shared/components/state-view/StateView';
import { isEmptyOrNull } from '@/shared/utils/string';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

type Props = AppNativeStackScreenProps<
  typeof ProfileSettingRoute.OrganizationEdit
>;

const MANAGER_ROLES = [ClientOrganizationProfileRoleEnum.Admin];

export const OrganizationEditScreen: FC<Props> = ({ route, navigation }) => {
  const { t } = useTranslation(LocalizationNameSpace.PROFILE_SETTING, {
    keyPrefix: 'screens.organization-edit'
  });
  const theme = useAppTheme();
  const { showAlert } = usePopup();
  const { organizationId } = route.params;

  const {
    data: organization,
    isLoading,
    isError
  } = useGetOrganizationById(organizationId);
  const { mutateAsync: updateOrganization, isPending } =
    useUpdateOrganization();
  const { mutate: deleteOrganization, isPending: isDeleting } =
    useDeleteOrganization();

  const goToDetail = useCallback(() => {
    navigation.popTo(ProfileSettingRoute.OrganizationDetail, {
      id: organizationId
    });
  }, [navigation, organizationId]);

  const handleSave = useCallback(
    async (
      payload: OrganizationEditSubmit | TerritorialAuthorityEditSubmit
    ) => {
      const existingAddress = organization?.address;
      // Only send `address` when there's already a persisted address row we can
      // update: the backend's JPA layer treats a non-null `id` as a lookup, so
      // an empty-string id triggers a 500. Creating a brand-new address from
      // the client is not currently supported — see the backend prompt.
      const addressPayload =
        payload.address !== undefined && existingAddress?.id
          ? { ...existingAddress, name: payload.address }
          : undefined;

      const clientOrganizationUpdateRequest: ClientOrganizationUpdateRequest = {
        name: payload.name,
        address: addressPayload,
        url: payload.url ?? '',
        phone: payload.phone ?? '',
        emailAddress: payload.emailAddress ?? '',
        description: payload.description ?? '',
        logo: payload.logo,
        detailImages: payload.detailImages,
        facts: payload.facts
      };
      if (payload.data) {
        clientOrganizationUpdateRequest.data = payload.data;
      }
      await updateOrganization({
        organizationId,
        clientOrganizationUpdateRequest
      });
      goToDetail();
    },
    [goToDetail, organization?.address, organizationId, updateOrganization]
  );

  const handleDelete = useCallback(() => {
    showAlert({
      dismissable: true,
      confirmColor: theme.colors.error,
      labels: t('alerts.confirm-delete', { returnObjects: true }),
      onConfirm: () =>
        deleteOrganization(
          { organizationId },
          {
            onSuccess: () =>
              navigation.popTo(AppRoute.Tabs, { screen: TabRoute.Profile })
          }
        )
    });
  }, [
    deleteOrganization,
    navigation,
    organizationId,
    showAlert,
    t,
    theme.colors.error
  ]);

  useEffect(() => {
    navigation.setOptions({
      header: () => (
        <NavigationBar
          type={NavBarType.MODAL_WITH_CLOSE}
          disableBackButton={isPending || isDeleting}
          onBackButtonPress={goToDetail}
        />
      )
    });
  }, [navigation, isPending, isDeleting, goToDetail]);

  const styles = editStyles;

  return (
    <>
      <OrganizationAccessGuard
        organization={organization}
        roles={MANAGER_ROLES}
        fallback={
          <StateView
            isEmpty
            stateLabels={{
              empty: t('state.noAccess', { returnObjects: true })
            }}
            contentContainerStyle={styles.content}
          />
        }
      >
        <StateView
          isLoading={isLoading || isEmptyOrNull(organization?.id)}
          isError={isError}
          stateLabels={t('state', { returnObjects: true })}
          contentContainerStyle={styles.content}
          keyboardAware
          keyboardAwareMode="layout"
          keyboardBottomOffset={24}
          gestureScroll
          keyboardDismissMode="none"
          keyboardShouldPersistTaps="always"
        >
          {organization && (
            <OrganizationAccessGuard
              requiredTags={[ClientOrganizationTagsEnum.TerritorialAuthority]}
              ignoreRoles
              fallback={
                <OrganizationEditForm
                  organization={organization}
                  onSave={handleSave}
                  onDelete={handleDelete}
                />
              }
            >
              <TerritorialAuthorityEditForm
                organization={organization}
                onSave={handleSave}
                onDelete={handleDelete}
              />
            </OrganizationAccessGuard>
          )}
        </StateView>
      </OrganizationAccessGuard>
    </>
  );
};

const editStyles = StyleSheet.create({
  content: { padding: 16, gap: 16 }
});
